import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TYPES = ["campus", "clinic", "office", "warehouse", "remote"];

export default function RoutingRules() {
  const [providers, setProviders] = useState([]);
  const [providerKey, setProviderKey] = useState("");
  const [rules, setRules] = useState([]);
  const [locations, setLocations] = useState([]);
  const [previewSite, setPreviewSite] = useState("");
  const [previewService, setPreviewService] = useState("");
  const [preview, setPreview] = useState(null);
  const [load, setLoad] = useState([]);
  const [draft, setDraft] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const loadOverview = useCallback(() => {
    apiFetch("/routing/rules").then(async (r) => {
      if (r.status === 403) { setError("You do not have administration access (config.read)."); return; }
      const data = await r.json();
      setProviders(data);
      if (data.length && !providerKey) setProviderKey(data[0].provider_key);
    });
  }, [providerKey]);

  useEffect(() => {
    if (!getToken()) { navigate("/login"); return; }
    loadOverview();
    apiFetch("/locations").then((r) => (r.ok ? r.json() : [])).then(setLocations);
  }, [navigate, loadOverview]);

  const provider = providers.find((p) => p.provider_key === providerKey);

  useEffect(() => {
    if (!provider) return;
    setRules(provider.rules.map((r) => ({ ...r })));
    setPreviewService(provider.services[0] || "");
    setDraft(null);
    setPreview(null);
    apiFetch(`/routing/queue-load/${provider.provider_key}`)
      .then((r) => (r.ok ? r.json() : { queues: [] })).then((d) => setLoad(d.queues));
  }, [providerKey, providers]);

  const setRule = (i, patch) =>
    setRules(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const toggle = (i, field, value) => {
    const list = rules[i][field] || [];
    setRule(i, { [field]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] });
  };

  const runPreview = () => {
    apiFetch("/routing/rules/preview", {
      method: "POST",
      body: JSON.stringify({ provider_key: providerKey, service_key: previewService,
                             rules, location_key: previewSite || null }),
    }).then((r) => (r.ok ? r.json() : null)).then(setPreview);
  };

  const saveDraft = () => {
    setStatus(null);
    apiFetch(`/routing/rules/${providerKey}/draft`, {
      method: "POST", body: JSON.stringify({ rules }),
    }).then(async (r) => {
      const body = await r.json();
      if (!r.ok) { setStatus(`Not saved — ${JSON.stringify(body.detail)}`); return; }
      setDraft(body);
      setStatus(`Draft v${body.config_version} saved. Drafts never affect live routing.`);
    });
  };

  const act = (action) => {
    apiFetch(`/config/${draft.record_id}/${action}`, { method: "POST" }).then(async (r) => {
      const body = await r.json();
      if (!r.ok) { setStatus(`${action} failed — ${JSON.stringify(body.detail)}`); return; }
      setStatus(action === "publish"
        ? `Published v${body.config_version}. New requests route with these rules.`
        : `Validated v${body.config_version}. Ready to publish.`);
      if (action === "publish") { setDraft(null); loadOverview(); }
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" data-testid="routing-title">Location Routing Rules</h1>
          <p className="text-sm text-muted-foreground">
            Map a requester's site to a provider queue. Edit, preview, then publish — no code.
          </p>
        </div>
        <Button asChild variant="outline" size="sm" data-testid="routing-admin-link">
          <Link to="/admin">Administration</Link>
        </Button>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-8">
        {error && <p className="text-sm text-destructive" data-testid="routing-error">{error}</p>}

        <section className="space-y-2">
          <label className="text-sm font-medium" htmlFor="provider-select">Service provider</label>
          <select id="provider-select" data-testid="routing-provider-select"
                  className="block w-full max-w-sm border rounded-md px-3 py-2 text-sm bg-background"
                  value={providerKey} onChange={(e) => setProviderKey(e.target.value)}>
            {providers.map((p) => (
              <option key={p.provider_key} value={p.provider_key}>{p.name}</option>
            ))}
          </select>
          {provider && (
            <p className="text-sm text-muted-foreground" data-testid="routing-queues">
              Declared queues: {provider.queues.join(", ") || "none"} · default{" "}
              {provider.default_queue} · published v{provider.published_version}
            </p>
          )}
        </section>

        {provider && (
          <section className="space-y-4" data-testid="routing-rules-editor">
            <h2 className="text-lg font-medium">Rules (evaluated in order)</h2>
            {rules.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="routing-rules-empty">
                No rules: every request falls back to the provider default queue.
              </p>
            )}
            {rules.map((rule, i) => (
              <div key={i} className="border rounded-md p-4 space-y-3" data-testid={`routing-rule-${i}`}>
                <div className="flex items-center gap-3">
                  <label className="text-sm" htmlFor={`rule-queue-${i}`}>Queue</label>
                  <select id={`rule-queue-${i}`} data-testid={`routing-rule-queue-${i}`}
                          className="border rounded-md px-2 py-1 text-sm bg-background"
                          value={rule.queue || ""} onChange={(e) => setRule(i, { queue: e.target.value })}>
                    <option value="">select…</option>
                    {provider.queues.map((q) => <option key={q} value={q}>{q}</option>)}
                  </select>
                  <Button variant="ghost" size="sm" data-testid={`routing-rule-remove-${i}`}
                          onClick={() => setRules(rules.filter((_, idx) => idx !== i))}>
                    Remove
                  </Button>
                </div>
                <fieldset>
                  <legend className="text-sm font-medium">Match these sites</legend>
                  <div className="flex flex-wrap gap-3 mt-1">
                    {locations.map((loc) => (
                      <label key={loc.key} className="text-sm flex items-center gap-1">
                        <input type="checkbox" data-testid={`routing-rule-${i}-site-${loc.key}`}
                               checked={(rule.location_keys || []).includes(loc.key)}
                               onChange={() => toggle(i, "location_keys", loc.key)} />
                        {loc.name}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <fieldset>
                  <legend className="text-sm font-medium">Or match these site types</legend>
                  <div className="flex flex-wrap gap-3 mt-1">
                    {TYPES.map((t) => (
                      <label key={t} className="text-sm flex items-center gap-1">
                        <input type="checkbox" data-testid={`routing-rule-${i}-type-${t}`}
                               checked={(rule.location_types || []).includes(t)}
                               onChange={() => toggle(i, "location_types", t)} />
                        {t}
                      </label>
                    ))}
                  </div>
                </fieldset>
                <Input placeholder="Why this rule exists" value={rule.note || ""}
                       data-testid={`routing-rule-note-${i}`}
                       onChange={(e) => setRule(i, { note: e.target.value })} />
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" data-testid="routing-rule-add"
                      onClick={() => setRules([...rules, { queue: "", location_keys: [], location_types: [] }])}>
                Add rule
              </Button>
              <Button size="sm" data-testid="routing-save-draft" onClick={saveDraft}>
                Save as draft
              </Button>
              {draft && (
                <>
                  <Button variant="outline" size="sm" data-testid="routing-validate"
                          onClick={() => act("validate")}>Validate</Button>
                  <Button variant="outline" size="sm" data-testid="routing-publish"
                          onClick={() => act("publish")}>Publish</Button>
                </>
              )}
            </div>
            {status && <p className="text-sm text-amber-800" data-testid="routing-status">{status}</p>}
          </section>
        )}

        {provider && (
          <section className="space-y-3" data-testid="routing-preview-panel">
            <h2 className="text-lg font-medium">Preview before publishing</h2>
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="text-sm block" htmlFor="preview-service">Catalog item</label>
                <select id="preview-service" data-testid="routing-preview-service"
                        className="border rounded-md px-2 py-1 text-sm bg-background"
                        value={previewService} onChange={(e) => setPreviewService(e.target.value)}>
                  {provider.services.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm block" htmlFor="preview-site">Requester's site</label>
                <select id="preview-site" data-testid="routing-preview-site"
                        className="border rounded-md px-2 py-1 text-sm bg-background"
                        value={previewSite} onChange={(e) => setPreviewSite(e.target.value)}>
                  <option value="">No site on the request</option>
                  {locations.map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}
                </select>
              </div>
              <Button size="sm" variant="outline" data-testid="routing-preview-run"
                      onClick={runPreview}>Preview queue</Button>
            </div>
            {preview && (
              <div className="border rounded-md p-4 text-sm space-y-1" data-testid="routing-preview-result">
                <p>
                  Proposed rules send this to <span className="font-semibold"
                    data-testid="routing-preview-queue">{preview.queue}</span>
                </p>
                <p className="text-muted-foreground" data-testid="routing-preview-reason">
                  {preview.reason}
                </p>
                <p className="text-muted-foreground" data-testid="routing-preview-published">
                  Live rules today would send it to {preview.published_result.queue}
                </p>
                {preview.validation_errors.length > 0 && (
                  <p className="text-destructive" data-testid="routing-preview-errors">
                    {preview.validation_errors.join("; ")}
                  </p>
                )}
                {preview.service_override_in_effect && (
                  <p className="text-amber-800" data-testid="routing-preview-override">
                    This catalog item declares its own routing, which overrides the provider rules.
                  </p>
                )}
              </div>
            )}
            <div>
              <h3 className="text-sm font-semibold mb-1">Open work per queue today</h3>
              <ul className="text-sm" data-testid="routing-queue-load">
                {load.map((q) => (
                  <li key={q.queue} data-testid={`routing-queue-load-${q.queue}`}>
                    {q.queue}: {q.open} open
                  </li>
                ))}
                {load.length === 0 && <li className="text-muted-foreground">No open work.</li>}
              </ul>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
